import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { useIsResponsive } from '@/hooks'
import { EmtTypography, EmtModal, EmtButton } from 'emt-components-v2'
import { ServiceSection } from './ServiceSection'
import { useServiceSlots } from '@/hooks/useServiceSlots'
import { getSlotType, makeBlankSlot } from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'

interface EditServiceModalProps {
  open: boolean
  setOpen: (open: boolean) => void
}

const EditServiceModal: React.FC<EditServiceModalProps> = ({ open, setOpen }) => {
  const [isMobile] = useIsResponsive()
  const storeSlotsByService = useNewRequestCartStore((s) => s.formSlotsByService)
  const [draftSlotsByService, setDraftSlotsByService] = useState(storeSlotsByService)
  const setFormSlotsByService = useNewRequestCartStore((s) => s.setFormSlotsByService)
  const selectedService = useNewRequestCartStore((s) => s.selectedService)
  const setSelectedService = useNewRequestCartStore((s) => s.setSelectedService)
  const [loading, setLoading] = useState(false)
  const formRefs = useRef<Record<string, any>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [localSelectedService, setLocalSelectedService] = useState<string | null>(selectedService || null)

  const { currentSlug, addNewSlot, handleSaveSlot, removeSlot, updateFormSlotsByService } = useServiceSlots({
    actualService: localSelectedService,
    formSlotsByService: draftSlotsByService,
    updateFormSlotsByService: (updater) => setDraftSlotsByService(updater as any),
  })

  const errorCheck = () => {
    let allOK = true
    const newErrors: Record<string, string> = {}

    if (currentSlug) {
      for (const slot of (draftSlotsByService as any)?.[currentSlug] ?? []) {
        const slotRef = formRefs.current[slot.id]
        if (slotRef?.validateOnly) {
          const ok = slotRef.validateOnly()
          if (!ok) allOK = false
        }
      }
    }

    setErrors(newErrors)
    return allOK
  }

  const handleValidate = async () => {
    setLoading(true)

    const slotsValid = errorCheck()

    if (!slotsValid) {
      setLoading(false)
      return
    }

    setSelectedService(localSelectedService)
    setFormSlotsByService(draftSlotsByService)

    setOpen(false)
    setLoading(false)
  }

  useEffect(() => {
    if (!currentSlug) return

    const currentSlots = draftSlotsByService?.[currentSlug] ?? []
    if (currentSlots.length > 0) return

    updateFormSlotsByService((prev: any) => ({
      ...prev,
      [currentSlug]: [makeBlankSlot(currentSlug, null)],
    }))
  }, [currentSlug])

  useEffect(() => {
    setLocalSelectedService(selectedService || null)
  }, [selectedService])

  return (
    <EmtModal
      open={open}
      disableScrollLock
      onClose={(event: React.MouseEvent) => {
        event.stopPropagation()
        setLocalSelectedService(selectedService || null)
        setOpen(false)
      }}
      sx={{
        '& .inner-box': { width: '700px', maxWidth: '90vw' },
        '& .children-box': { padding: isMobile ? '20px' : '40px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
        }}
      >
        <EmtTypography variant="h4" fontWeight={800}>
          Modifier le service
        </EmtTypography>
        {/* {localSelectedService && ( */}
        <ServiceSection
          isMobile={isMobile}
          selectedService={localSelectedService}
          formSlotsByService={draftSlotsByService}
          formRefs={formRefs}
          localStorageInitialValues={null}
          onServiceChange={(value) => {
            const slug = getSlotType(value)
            setLocalSelectedService(slug)
          }}
          onAddNewSlot={addNewSlot}
          onSaveSlot={handleSaveSlot}
          onRemoveSlot={removeSlot}
          showSelect
          addButton={false}
          showGardeTimes={false}
          errors={errors}
        />
        {/* )} */}
        <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <EmtButton
            fullWidth
            variant="outlined"
            size="medium"
            color="purple"
            onClick={() => {
              setLocalSelectedService(selectedService || null)
              setOpen(false)
            }}
          >
            Annuler
          </EmtButton>
          <EmtButton fullWidth size="medium" color="purple" onClick={handleValidate} loader={loading}>
            Valider
          </EmtButton>
        </Box>
      </Box>
    </EmtModal>
  )
}

export default EditServiceModal
